import { useEffect } from 'react';
import { useSyncStore } from '@/offline/sync';
import { useStorageQuotaStore } from '@/offline/storage-quota';
import { AlertTriangle, RefreshCw, HardDrive, KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * SyncStatusBanner — Sprint-3 Findings 22, 39, 62.
 *
 * Persistent top-of-app banner that surfaces three conditions the
 * medic absolutely needs to see before ending a shift:
 *
 *   - failedCount > 0   → "N records failed to sync" with a link to
 *                          the admin failed-syncs view.
 *   - oldestPendingSec > 1h → "Sync stale: N records pending the
 *                              longest H:MM:SS" so an unattended
 *                              offline-mode session doesn't silently
 *                              accumulate hours of unsynced records.
 *   - quota warning / exceeded → "Storage X% full" / "Local storage
 *                                  full — sync to free space".
 *   - refreshingAuth      → transient "re-authenticating…" pip.
 *
 * The banner is intentionally NOT a modal — it sits in the existing
 * top-bar real estate so the medic can keep documenting. It only
 * paints when there's actually something to surface.
 */
export function SyncStatusBanner() {
  const { failedCount, oldestPendingSec, refreshingAuth, syncAll, online } = useSyncStore();
  const quota = useStorageQuotaStore();

  // Light periodic refresh of the storage estimate when the banner
  // is visible (the global poll runs every 60s; this gives the
  // banner a quicker update right after a sync clears space).
  useEffect(() => {
    const t = window.setInterval(() => { void quota.refresh(); }, 30_000);
    return () => window.clearInterval(t);
  }, [quota]);

  const showFailed = failedCount > 0;
  const showStale = (oldestPendingSec ?? 0) > 60 * 60; // >1h
  const showQuotaExceeded = quota.exceeded;
  const showQuotaWarn = !quota.exceeded && quota.warning;
  const showRefreshing = refreshingAuth;

  if (!showFailed && !showStale && !showQuotaExceeded && !showQuotaWarn && !showRefreshing) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-amber-700 bg-amber-950/70 px-3 py-2 text-xs text-amber-100">
      {showQuotaExceeded && (
        <span className="flex items-center gap-1.5 font-bold text-red-300">
          <HardDrive size={14} />
          Local storage full — sync now to free space, then continue.
        </span>
      )}

      {showFailed && (
        <span className="flex items-center gap-1.5">
          <AlertTriangle size={14} className="text-red-300" />
          <strong>{failedCount}</strong> record{failedCount === 1 ? '' : 's'} failed to sync —
          <Link to="/admin/data-pipeline" className="ml-1 underline hover:text-amber-300">
            view failed syncs
          </Link>
        </span>
      )}

      {showStale && oldestPendingSec !== null && (
        <span className="flex items-center gap-1.5">
          <RefreshCw size={14} />
          Sync stale — oldest pending: {formatDuration(oldestPendingSec)}
          {online && (
            <button
              type="button"
              onClick={() => { void syncAll(); }}
              className="ml-1 rounded border border-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest hover:bg-amber-900"
            >
              Sync now
            </button>
          )}
        </span>
      )}

      {showQuotaWarn && quota.fraction !== null && (
        <span className="flex items-center gap-1.5 text-amber-200">
          <HardDrive size={14} />
          Local storage {Math.round(quota.fraction * 100)}% full.
        </span>
      )}

      {showRefreshing && (
        <span className="flex items-center gap-1.5 text-sky-300">
          <KeyRound size={14} className="animate-pulse" />
          Re-authenticating…
        </span>
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
